import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import TopNavigation from 'components/ui/TopNavigation';
import QuickActionToolbar from 'components/ui/QuickActionToolbar';
import NotificationAlertBar from 'components/ui/NotificationAlertBar';
import Icon from 'components/AppIcon';

import KPISummaryBar from './components/KPISummaryBar';
import TimePeriodSelector from './components/TimePeriodSelector';
import TrendComparisonChart from './components/TrendComparisonChart';
import TerritoryPerformanceChart from './components/TerritoryPerformanceChart';
import ProductMixChart from './components/ProductMixChart';
import SalespersonRankingTable from './components/SalespersonRankingTable';
import CoverageGapPanel from './components/CoverageGapPanel';
import AlertsPanel from './components/AlertsPanel';
import DecliningAccountsTable from './components/DecliningAccountsTable';
import WeeklyPerformanceChart from './components/WeeklyPerformanceChart';
import FilterBar from './components/FilterBar';

export default function PerformanceAnalytics() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('Month');
  const [comparison, setComparison] = useState('YoY');
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState({ territory: 'All Territories', category: 'All Categories', team: 'All Teams' });
  const [exportToast, setExportToast] = useState(null);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'trends', label: 'Trends', icon: 'TrendingUp' },
    { id: 'gaps', label: 'Gap Analysis', icon: 'AlertTriangle' },
    { id: 'team', label: 'Team Performance', icon: 'Users' },
  ];

  const handleExport = (format) => {
    setExportToast(`Exporting report as ${format?.toUpperCase()}...`);
    setTimeout(() => setExportToast(null), 3000);
  };

  const handleAction = (action) => {
    if (action === 'export') handleExport('pdf');
    if (action === 'schedule') setExportToast('Report scheduled successfully.');
    if (action === 'share') setExportToast('Share link copied to clipboard.');
    setTimeout(() => setExportToast(null), 3000);
  };

  return (
    <div className="min-h-screen bg-background page-gradient-bg">
      <TopNavigation />
      <NotificationAlertBar />
      <QuickActionToolbar onAction={handleAction} />
      {/* Page Header */}
      <div className="px-4 md:px-6 lg:px-8 py-3 md:py-4 lg:py-6 border-b border-border bg-card chart-panel-depth">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Icon name="BarChart2" size={18} color="var(--color-primary)" strokeWidth={2} />
              <h1 className="font-heading font-bold text-lg sm:text-xl lg:text-2xl text-foreground">Performance Analytics</h1>
            </div>
            <p className="font-caption text-xs sm:text-sm text-muted-foreground">
              Comprehensive time-based analysis &amp; gap identification — as of {new Date()?.toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          <TimePeriodSelector period={period} setPeriod={setPeriod} comparison={comparison} setComparison={setComparison} />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mt-3 overflow-x-auto custom-scrollbar pb-1">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-caption text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap flex-shrink-0 min-h-[34px] ${
                activeTab === tab?.id
                  ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              style={activeTab === tab?.id ? { background: 'linear-gradient(135deg, #DC2626, #B91C1C)', boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)' } : {}}
            >
              <Icon name={tab?.icon} size={13} color="currentColor" strokeWidth={2} />
              {tab?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Main Content */}
      <div className="px-4 md:px-6 lg:px-8 py-3 md:py-4 lg:py-6 pb-20 lg:pb-8 space-y-3 md:space-y-4 lg:space-y-6">
        {/* Filter Bar */}
        <FilterBar filters={filters} setFilters={setFilters} onExport={handleExport} />

        {/* KPI Summary */}
        <KPISummaryBar period={period} />

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-4 lg:space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
              <div className="lg:col-span-2">
                <TrendComparisonChart period={period} comparison={comparison} />
              </div>
              <div>
                <ProductMixChart />
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
              <TerritoryPerformanceChart />
              <WeeklyPerformanceChart />
            </div>
            <AlertsPanel />
          </div>
        )}

        {activeTab === 'trends' && (
          <div className="space-y-4 lg:space-y-6">
            <TrendComparisonChart period={period} comparison={comparison} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
              <WeeklyPerformanceChart />
              <ProductMixChart />
            </div>
            <TerritoryPerformanceChart />
          </div>
        )}

        {activeTab === 'gaps' && (
          <div className="space-y-4 lg:space-y-6">
            <AlertsPanel />
            <CoverageGapPanel onNavigate={navigate} />
            <DecliningAccountsTable onNavigate={navigate} />
          </div>
        )}

        {activeTab === 'team' && (
          <div className="space-y-4 lg:space-y-6">
            <SalespersonRankingTable />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
              <TerritoryPerformanceChart />
              <AlertsPanel />
            </div>
          </div>
        )}
      </div>
      {/* Export Toast */}
      {exportToast && (
        <div className="fixed bottom-24 lg:bottom-6 left-1/2 -translate-x-1/2 z-[200] bg-foreground text-background px-5 py-3 rounded-lg shadow-xl flex items-center gap-3 font-caption text-sm">
          <Icon name="CheckCircle" size={16} color="var(--color-success)" strokeWidth={2.5} />
          {exportToast}
        </div>
      )}
    </div>
  );
}